import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";

/**
 * Web Speech API microphone button.
 * - Appends recognised speech to the textarea value via `onTranscript(text)`.
 * - Uses fr-CA (Quebec French) by default, falls back to navigator language.
 * - Shows a friendly tooltip when the API is not supported (Firefox).
 */
export default function MicButton({ onTranscript, lang = "fr-CA", disabled = false, label, size = "md" }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const recRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const rec = new SR();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (e) => {
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
      }
      if (final) onTranscript(final.trim());
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;
    return () => { try { rec.stop(); } catch { /* ignore */ } };
  }, [lang, onTranscript]);

  const toggle = () => {
    const rec = recRef.current;
    if (!rec) return;
    if (listening) {
      try { rec.stop(); } catch { /* ignore */ }
      setListening(false);
    } else {
      try { rec.start(); setListening(true); } catch { /* already started */ }
    }
  };

  const isFr = lang.startsWith("fr");
  const padSize = size === "sm" ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm";

  if (!supported) {
    return (
      <button
        type="button"
        disabled
        title={isFr
          ? "Reconnaissance vocale non supportée par ce navigateur — utilisez Chrome, Edge ou Safari."
          : "Speech recognition not supported in this browser — try Chrome, Edge or Safari."}
        className={`btn-ghost ${padSize} opacity-60 cursor-not-allowed`}
        data-testid="mic-unsupported"
      >
        <MicOff size={14} /> {label || (isFr ? "Micro" : "Mic")}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      className={`${listening ? "btn-red" : "btn-ghost"} ${padSize} relative`}
      data-testid="mic-button"
      aria-pressed={listening}
      title={listening
        ? (isFr ? "Cliquez pour arrêter la dictée" : "Click to stop dictation")
        : (isFr ? "Dicter la description" : "Dictate description")}
    >
      <Mic size={14} />
      <span>
        {listening
          ? (isFr ? "Écoute…" : "Listening…")
          : (label || (isFr ? "Dicter" : "Dictate"))}
      </span>
      {listening && (
        <span
          aria-hidden
          className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full"
          style={{ background: "#FF3B3B", boxShadow: "0 0 8px #FF3B3B", animation: "pulse 1.2s ease-in-out infinite" }}
        />
      )}
    </button>
  );
}
